/* global BigInt */
const normalizedSportSlug = (sport) => String(
  typeof sport === 'object' ? sport?.slug ?? '' : sport ?? '',
).trim().toLowerCase().replace(/_/g, '-');

const publicSportSlug = (sport) => {
  const slug = normalizedSportSlug(sport);
  return slug === 'hockey' ? 'ice-hockey' : slug;
};

const rawFixtureIdentity = fixture => (
  fixture?.fixtureId
  ?? fixture?.fixture_id
  ?? fixture?.id
  ?? fixture?.matchId
  ?? fixture?.match_id
);

const positiveCanonicalId = (value) => {
  if (value == null) return null;
  const token = String(value).trim();
  if (!/^\d+$/.test(token)) return null;
  try {
    return BigInt(token) > 0n ? BigInt(token).toString() : null;
  } catch {
    return null;
  }
};

const fixtureIdentity = fixture => positiveCanonicalId(rawFixtureIdentity(fixture));
const hasDimensionFilter = value => value != null && String(value).trim() !== '';
const authoritativeTotal = value => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const completeOnePage = ({
  fixtures,
  page,
  totalPages,
  listTotalItems,
  listSnapshotComplete = false,
}) => {
  const total = authoritativeTotal(listTotalItems);
  if (!Array.isArray(fixtures)
    || Number(page) !== 1
    || Number(totalPages) !== 1
    || total == null) return null;

  const identities = fixtures.map(fixtureIdentity);
  if (identities.some(identity => identity == null)) return null;
  const unique = new Set(identities);
  if (unique.size !== total && listSnapshotComplete !== true) return null;
  return { identities, total, visibleTotal: unique.size };
};

export const sportsnewFixtureGameTitleId = fixture => (
  fixture?.esports?.gameTitleId
  ?? fixture?.esports?.game_title_id
  ?? fixture?.gameTitleId
  ?? fixture?.game_title_id
  ?? fixture?.esportsGameTitleId
  ?? fixture?.esports_game_title_id
  ?? null
);

export const sportsnewFixtureSportSlug = fixture => publicSportSlug(
  fixture?.sport ?? fixture?.sportSlug ?? fixture?.sport_slug ?? '',
);

/**
 * The resolved list total is the exact count authority for the currently
 * selected, unfiltered sport. Navigation remains authoritative for every
 * unselected sport and for league/game/tournament drill-down totals.
 */
export const selectedSportsnewRailCount = ({
  sportSlug,
  selectedSport,
  selectedLeagueId,
  selectedGameTitleId,
  selectedTournamentId,
  listReady,
  listMembershipRevisionPending,
  listTotalItems,
  fixtures,
  page,
  totalPages,
  listSnapshotComplete,
} = {}) => {
  if (!listReady) return null;
  if ([selectedLeagueId, selectedGameTitleId, selectedTournamentId].some(hasDimensionFilter)) return null;
  if (!publicSportSlug(selectedSport)) return null;
  if (publicSportSlug(selectedSport) !== publicSportSlug(sportSlug)) return null;
  const complete = completeOnePage({
    fixtures,
    page,
    totalPages,
    listTotalItems,
    listSnapshotComplete,
  });
  if (complete) {
    const selectedSlug = publicSportSlug(selectedSport);
    const visibleIds = new Set();
    for (const fixture of fixtures) {
      if (sportsnewFixtureSportSlug(fixture) !== selectedSlug) continue;
      const id = fixtureIdentity(fixture);
      if (id != null) visibleIds.add(id);
    }
    return visibleIds.size;
  }
  if (listMembershipRevisionPending) return null;
  return authoritativeTotal(listTotalItems);
};

/**
 * A complete one-page eSports list can immediately reconcile game badges
 * while a navigation aggregate is catching up. Multi-page and drill-down
 * lists remain aggregate-owned because their visible rows are only a subset.
 */
export const sportsnewEsportsGameCountOverrides = ({
  fixtures,
  selectedSport,
  selectedLeagueId,
  selectedGameTitleId,
  selectedTournamentId,
  listReady,
  page,
  totalPages,
  listTotalItems,
  listSnapshotComplete,
} = {}) => {
  if (!listReady) return null;
  if (normalizedSportSlug(selectedSport) !== 'esports') return null;
  if ([selectedLeagueId, selectedGameTitleId, selectedTournamentId].some(hasDimensionFilter)) return null;
  const complete = completeOnePage({
    fixtures,
    page,
    totalPages,
    listTotalItems,
    listSnapshotComplete,
  });
  if (!complete) return null;

  const fixtureBuckets = new Map();
  const counts = new Map();
  for (const fixture of fixtures) {
    const sport = normalizedSportSlug(
      fixture?.sport ?? fixture?.sportSlug ?? fixture?.sport_slug ?? '',
    );
    const fixtureId = fixtureIdentity(fixture);
    const key = positiveCanonicalId(sportsnewFixtureGameTitleId(fixture));
    if (sport !== 'esports' || fixtureId == null || key == null) return null;
    const previousBucket = fixtureBuckets.get(fixtureId);
    if (previousBucket != null && previousBucket !== key) return null;
    if (previousBucket != null) continue;
    fixtureBuckets.set(fixtureId, key);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  if (fixtureBuckets.size !== complete.visibleTotal) return null;
  return counts;
};

/**
 * When the unfiltered list fits on one page, every rendered fixture is present
 * locally and can immediately own all sport badges. This avoids displaying a
 * stale navigation aggregate after a newly-added fixture has already appeared
 * in the central list. A paginated list intentionally returns null.
 */
export const sportsnewAllSportCountOverrides = ({
  fixtures,
  selectedSport,
  selectedLeagueId,
  selectedGameTitleId,
  selectedTournamentId,
  listReady,
  page,
  totalPages,
  listTotalItems,
  listSnapshotComplete,
} = {}) => {
  if (!listReady) return null;
  if (normalizedSportSlug(selectedSport)) return null;
  if ([selectedLeagueId, selectedGameTitleId, selectedTournamentId].some(hasDimensionFilter)) return null;
  const complete = completeOnePage({
    fixtures,
    page,
    totalPages,
    listTotalItems,
    listSnapshotComplete,
  });
  if (!complete) return null;

  const fixtureBuckets = new Map();
  const counts = new Map();
  for (const fixture of fixtures) {
    const sportSlug = sportsnewFixtureSportSlug(fixture);
    const fixtureId = fixtureIdentity(fixture);
    if (!sportSlug || fixtureId == null) return null;
    const previousBucket = fixtureBuckets.get(fixtureId);
    if (previousBucket != null && previousBucket !== sportSlug) return null;
    if (previousBucket != null) continue;
    fixtureBuckets.set(fixtureId, sportSlug);
    counts.set(sportSlug, (counts.get(sportSlug) ?? 0) + 1);
  }
  if (fixtureBuckets.size !== complete.visibleTotal) return null;
  return counts;
};

export default Object.freeze({
  selectedSportsnewRailCount,
  sportsnewAllSportCountOverrides,
  sportsnewEsportsGameCountOverrides,
  sportsnewFixtureGameTitleId,
  sportsnewFixtureSportSlug,
});
